/**
 * PanicButton · v2.6.7 batch J — right-edge swipe-out tab.
 *
 * Per user request: the giant top-bar panic button was too large. Replaced
 * with a small SIREN icon fixed to the right edge of the viewport — always
 * visible across rider/driver pages, never blocks content, opens the same
 * confirmation dialog → SOS alarm → supervisor SMS → 911 dial sequence.
 *
 * Interactions:
 *   - Tap the small square → opens the confirmation dialog
 *   - Swipe LEFT on the tab → reveals an inline "TAP TO CALL 911" wedge
 *     that animates out from the right edge (mobile-friendly gesture)
 *   - Tap anywhere else → wedge auto-collapses
 *
 * On confirm: GPS capture → /api/panic → SOS alarm + supervisor SMS fan-out →
 * tel:911 dialer. SMS-to-personal-contacts removed (911 + supervisors only).
 */
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Siren, Phone, Loader2, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SOS_ALARM = "/sounds/panic-sos-emergency-alert.mp3";

// AUTOPLAY UNLOCK — v2.6.7 fix.
// We prime the browser autoplay policy with a guaranteed-silent inline WAV
// data URI so the prime can NEVER produce audible sound (previous fix with
// volume=0/muted=true on the real SOS file still emitted blips on iOS Safari).
// The real SOS alarm element is constructed fresh when the panic button is
// confirmed — the browser allows it because a prior audio.play() succeeded
// under the same user-gesture session.
const SILENT_WAV_DATA_URI =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";

let _sosUnlocked = false;
function primeSos() {
  if (_sosUnlocked) return;
  try {
    const el = new Audio(SILENT_WAV_DATA_URI);
    el.volume = 0.0;
    el.muted = true;
    const p = el.play();
    if (p && p.then) {
      p.then(() => { try { el.pause(); } catch {} _sosUnlocked = true; })
       .catch(() => { /* still blocked — try again next gesture */ });
    } else {
      _sosUnlocked = true;
    }
  } catch { /* noop */ }
}
if (typeof window !== "undefined") {
  const unlock = () => primeSos();
  const opts = { once: true, capture: true };
  window.addEventListener("pointerdown", unlock, opts);
  window.addEventListener("keydown", unlock, opts);
  window.addEventListener("touchstart", unlock, opts);
}

function captureGps() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 },
    );
  });
}

export default function PanicButton({ compact = false, hide = false }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [alarming, setAlarming] = useState(false);
  // v2.6.7 batch J — right-edge swipe-out wedge state.
  // expanded === true when the user swipes the tab leftward; auto-collapses
  // after 4 seconds or on any outside tap.
  const [expanded, setExpanded] = useState(false);
  const touchStartXRef = useRef(null);
  const audioRef = useRef(null);

  // Stop the alarm when the component unmounts so it doesn't loop forever.
  useEffect(() => () => stopAlarm(), []);

  // Auto-collapse the wedge after 4 seconds of no activity.
  useEffect(() => {
    if (!expanded) return;
    const t = setTimeout(() => setExpanded(false), 4000);
    return () => clearTimeout(t);
  }, [expanded]);

  const startAlarm = () => {
    if (!audioRef.current) {
      try { const a = new Audio(SOS_ALARM); a.loop = true; a.volume = 1.0; audioRef.current = a; }
      catch { audioRef.current = null; }
    }
    const a = audioRef.current;
    if (!a) return;
    try { a.currentTime = 0; a.muted = false; a.volume = 1.0; const p = a.play(); if (p && p.catch) p.catch(()=>{}); setAlarming(true); } catch {}
  };
  const stopAlarm = () => {
    const a = audioRef.current;
    if (a) { try { a.pause(); a.currentTime = 0; } catch {} }
    setAlarming(false);
  };

  // v2.6.9 batch BK · 3-second hold-to-trigger gesture on the confirm step.
  // Prevents accidental panic activation (the previous tap-to-confirm flow
  // was too easy to trigger by mistake). Progress fills during the hold;
  // releasing early cancels. Holding the full 3 s fires `triggerEmergency`.
  const HOLD_MS = 3000;
  const [holdProgress, setHoldProgress] = useState(0);    // 0..1
  const holdTimerRef = useRef(null);
  const holdStartRef = useRef(0);
  const holdCancelledRef = useRef(false);
  const startHold = () => {
    if (busy) return;
    holdCancelledRef.current = false;
    holdStartRef.current = Date.now();
    setHoldProgress(0);
    const tick = () => {
      if (holdCancelledRef.current) return;
      const elapsed = Date.now() - holdStartRef.current;
      const pct = Math.min(1, elapsed / HOLD_MS);
      setHoldProgress(pct);
      if (pct >= 1) {
        holdTimerRef.current = null;
        triggerEmergency();
      } else {
        holdTimerRef.current = window.requestAnimationFrame(tick);
      }
    };
    holdTimerRef.current = window.requestAnimationFrame(tick);
  };
  const cancelHold = () => {
    holdCancelledRef.current = true;
    if (holdTimerRef.current) {
      window.cancelAnimationFrame(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    setHoldProgress(0);
  };
  // Auto-cancel on dialog close
  useEffect(() => {
    if (!open) cancelHold();
  }, [open]);

  if (hide) return null;

  const triggerEmergency = async () => {
    setBusy(true);
    // Start the alarm IMMEDIATELY so the surrounding environment can hear it.
    startAlarm();
    try {
      const gps = await captureGps();
      const { data } = await api.post("/panic", { lat: gps?.lat, lng: gps?.lng });
      const sentN = data.sms_sent_to?.length || 0;
      toast.warning(`SOS · ${sentN} supervisor${sentN === 1 ? "" : "s"} notified · Dialing 911`, { duration: 5000 });
    } catch (err) {
      // Even if our backend errs, ALWAYS proceed to dial 911 — saving life > clean state
      toast.error("SOS broadcast partial — dialing 911 anyway");
    } finally {
      setBusy(false);
      setOpen(false);
      setTimeout(() => { window.location.href = "tel:911"; }, 200);
    }
  };

  return (
    <>
      {alarming && (
        <div className="fixed top-2 right-2 z-50 flex items-center gap-2 bg-red-600/95 border border-red-300/60 rounded-md shadow-lg shadow-red-600/40 px-3 py-2" data-testid="sos-alarm-strip">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-200 opacity-75"/>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-200"/>
          </span>
          <span className="text-xs font-bold uppercase tracking-wider text-white">SOS ACTIVE</span>
          <Button data-testid="sos-stop-btn" size="icon" variant="ghost" onClick={stopAlarm} className="h-7 w-7 text-white hover:text-red-200">
            <VolumeX className="h-4 w-4"/>
          </Button>
        </div>
      )}

      {/* v2.6.7 batch J — right-edge swipe-out emergency tab.
          - Always visible at viewport mid-right
          - Tap to open dialog
          - Swipe left on the tab to expand into a CALL 911 wedge
          - Auto-collapses after 4 s */}
      <Dialog open={open} onOpenChange={setOpen}>
        <div
          className="fixed top-1/2 -translate-y-1/2 right-0 z-40 pointer-events-none"
          data-testid="panic-edge-anchor"
        >
          <div
            className={`pointer-events-auto flex items-stretch transition-transform duration-300 ease-out ${expanded ? "-translate-x-1" : ""}`}
            onTouchStart={(e) => { touchStartXRef.current = e.touches[0]?.clientX ?? null; }}
            onTouchEnd={(e) => {
              const start = touchStartXRef.current;
              touchStartXRef.current = null;
              if (start == null) return;
              const end = e.changedTouches[0]?.clientX ?? start;
              // Swipe LEFT (end < start) by at least 32 px → expand the wedge
              if (start - end > 32) setExpanded(true);
              // Swipe RIGHT (end > start) by 32 px → collapse
              if (end - start > 32) setExpanded(false);
            }}
          >
            {/* Wedge (revealed on swipe-left) */}
            {expanded && (
              <button
                type="button"
                onClick={() => { setExpanded(false); setOpen(true); }}
                data-testid="panic-wedge-call"
                className="bg-red-600 hover:bg-red-500 text-white font-extrabold uppercase tracking-[0.2em] text-[10px] px-3 rounded-l-md shadow-lg shadow-red-700/40 flex items-center gap-1.5 animate-in slide-in-from-right-2 duration-200"
              >
                <Phone className="h-3 w-3"/>Tap to Call 911
              </button>
            )}

            {/* Main square tab — always visible */}
            <DialogTrigger asChild>
              <button
                type="button"
                data-testid="panic-btn"
                aria-label="Emergency · Call 911"
                title="Emergency · 911"
                onClick={() => setExpanded(false)}
                className="relative w-11 h-11 flex items-center justify-center bg-red-600 hover:bg-red-500 active:bg-red-700 text-white shadow-lg shadow-red-700/40 rounded-l-md border-l border-y border-red-300/40 group"
              >
                {/* Soft pulse so the button is discoverable */}
                <span className="absolute inset-0 rounded-l-md bg-red-500/60 animate-ping opacity-30 pointer-events-none"/>
                <Siren className="h-5 w-5 relative z-10 group-active:scale-95 transition-transform"/>
                <span className="sr-only">Panic</span>
              </button>
            </DialogTrigger>
          </div>
        </div>

        <DialogContent className="bg-zinc-900 border-red-500/40 text-zinc-100">
          <DialogHeader><DialogTitle className="text-red-400 flex items-center gap-2"><Siren className="h-5 w-5"/>Confirm Emergency · 911</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-zinc-300">
            This will <span className="text-red-400 font-bold">notify all supervisors</span> with your name &amp; live GPS location, sound the <span className="text-red-400 font-bold">SOS alarm</span>, then open your phone's dialer with <span className="text-red-400 font-bold">911</span>.
          </DialogDescription>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button data-testid="panic-cancel-btn" variant="outline" onClick={()=>setOpen(false)} disabled={busy} className="bg-zinc-950 border-zinc-700">Cancel</Button>
            {/* v2.6.9 batch BK · 3-second hold-to-trigger. Prevents accidental
                panic activation — must press AND HOLD for 3 s. Release cancels. */}
            <button
              data-testid="panic-confirm-btn"
              data-hold-progress={holdProgress.toFixed(2)}
              type="button"
              disabled={busy}
              onPointerDown={startHold}
              onPointerUp={cancelHold}
              onPointerLeave={cancelHold}
              onPointerCancel={cancelHold}
              onTouchStart={startHold}
              onTouchEnd={cancelHold}
              onTouchCancel={cancelHold}
              className="relative overflow-hidden h-10 px-4 rounded-md bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-bold text-sm inline-flex items-center justify-center select-none"
            >
              {/* Fill bar — width tracks holdProgress (0→100%). When full, the
                  hold timer fires triggerEmergency() and the bar stays full. */}
              <span
                className="absolute inset-y-0 left-0 bg-white/30 transition-[width] duration-75 ease-linear pointer-events-none"
                style={{ width: `${holdProgress * 100}%` }}
                data-testid="panic-hold-progress"
              />
              <span className="relative z-10 flex items-center gap-1">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin"/>Notifying…</>
                  : holdProgress > 0
                    ? <>HOLD · {Math.ceil((1 - holdProgress) * 3)}s</>
                    : <><Volume2 className="h-4 w-4"/> HOLD TO CALL 911</>}
              </span>
            </button>
          </div>
          <p className="text-[10px] text-zinc-500 text-center mt-1">Press and hold the red button for 3 seconds. Release to cancel.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
